<template>
    <confirm-modal></confirm-modal>

    <form-modal
        :title="editDialogTitle"
        v-model="isShowEditEditor"
        :loading="loading"
        :width="600"
        @submit="saveRecord">
        <fieldset>
            <div class="col-form-label fw-bold mb-2">{{ agencyLabel }}</div>

            <div class="employee-form">
                <label
                    for="year"
                    class="col-form-label">
                    Year:
                </label>
                <div>
                    <b-input
                        v-if="!isEditMode && hasYearChoice"
                        name="year"
                        type="number"
                        :max-length="4"
                        :min="minYear"
                        :max="maxYear"
                        v-model="record.year"
                        :error="errors.year" />
                    <span
                        class="col-form-label d-block text-nowrap"
                        v-else>
                        {{ record.year }}
                        <span
                            class="form-text ms-1"
                            v-if="!isEditMode">
                            Only {{ record.year }} is available for this agency.
                        </span>
                    </span>
                </div>

                <label
                    for="swornInMale"
                    class="col-form-label">
                    Full-Time Sworn - Male:
                </label>
                <div>
                    <b-input
                        name="swornInMale"
                        type="number"
                        :min="0"
                        :max-length="5"
                        v-model="record.sworn_in_male"
                        :error="errors.sworn_in_male" />
                </div>
                <label
                    for="swornInFemale"
                    class="col-form-label">
                    Full-Time Sworn - Female:
                </label>
                <div>
                    <b-input
                        name="swornInFemale"
                        type="number"
                        :min="0"
                        :max-length="5"
                        v-model="record.sworn_in_female"
                        :error="errors.sworn_in_female" />
                </div>
                <label
                    for="civilianMale"
                    class="col-form-label">
                    Full-Time Civilian - Male:
                </label>
                <div>
                    <b-input
                        name="civilianMale"
                        type="number"
                        :min="0"
                        :max-length="5"
                        v-model="record.civilian_male"
                        :error="errors.civilian_male" />
                </div>
                <label
                    for="civilianFemale"
                    class="col-form-label">
                    Full-Time Civilian - Female:
                </label>
                <div>
                    <b-input
                        name="civilianFemale"
                        type="number"
                        :min="0"
                        :max-length="5"
                        v-model="record.civilian_female"
                        :error="errors.civilian_female" />
                </div>
            </div>

            <fieldset
                v-if="props.stateCode == 'CO'"
                class="border border-secondary rounded p-2 mt-2"
                aria-describedby="coOptionalFieldsNote">
                <legend class="visually-hidden">Proposition 130 eligibility fields</legend>
                <p
                    id="coOptionalFieldsNote"
                    class="ms-0 mt-0 me-0 mb-2 callout callout-info">The following fields are for eligibility for Proposition 130 law enforcement training funds and should only be completed by municipal police departments, sheriffs offices, marshals offices and municipal courts. All other agencies should leave them blank.</p>
                <div class="employee-form">
                    <label
                        for="certifiedOfficers"
                        class="col-form-label">
                        Certified Officers:
                    </label>
                    <div>
                        <b-input
                            name="certifiedOfficers"
                            type="text"
                            inputmode="numeric"
                            pattern="[0-9]*"
                            autocomplete="off"
                            :max-length="5"
                            v-model="record.certified_officers"
                            :error="errors.certified_officers" />
                    </div>
                    <label
                        for="nonCertifiedOfficers"
                        class="col-form-label">
                        Non-Certified Officers:
                    </label>
                    <div>
                        <b-input
                            name="nonCertifiedOfficers"
                            type="text"
                            inputmode="numeric"
                            pattern="[0-9]*"
                            autocomplete="off"
                            :max-length="5"
                            v-model="record.non_certified_officers"
                            :error="errors.non_certified_officers" />
                    </div>
                    <label
                        for="leaBudget"
                        class="col-form-label">
                        LEA Budget:
                    </label>
                    <div>
                        <b-input
                            name="leaBudget"
                            type="text"
                            inputmode="numeric"
                            autocomplete="off"
                            :max-length="12"
                            v-model="leaBudgetDisplay"
                            :error="errors.lea_budget" />
                    </div>
                </div>
            </fieldset>

            <b-alert
                class="mt-3"
                variant="danger"
                v-if="errors.total">
                {{ errors.total }}
            </b-alert>
        </fieldset>
    </form-modal>

    <div class="mb-3 row">
        <div class="col-sm-4">
            <label
                for="agency"
                class="form-label">
                Agency:
            </label>
            <b-select
                name="agency"
                v-model="selectedOri"
                :items="oriOptions"
                @update:modelValue="handleOriChange" />
        </div>
    </div>

    <b-alert
        variant="danger"
        v-if="oriListLoaded && oriOptions.length == 0">
        Error, no agencies submitting PE data have been found. Please contact your administrator.
    </b-alert>

    <b-grid
        v-else
        caption="Employee Data"
        :items="employeeDataStore.items"
        :columns="columns"
        :show-filter="false"
        :show-paging="false"
        :loading="loading">
        <template #beforeFilter>
            <b-button @click.prevent="showAddEditor">Add Record</b-button>
        </template>
        <template #cell.edit="{item}">
            <b-button
                @click.prevent="showEditEditor(item)"
                size="sm">
                Edit
            </b-button>
        </template>
        <template #cell.delete="{item}">
            <b-button
                @click.prevent="deleteRecord(item)"
                size="sm"
                variant="danger">
                Delete
            </b-button>
        </template>
        <template #cell.document="{item}">
            <b-button
                v-if="!item.file_id"
                @click.prevent="uploadDocument(item)"
                size="sm"
                variant="secondary">
                Upload Document
            </b-button>
            <div class="btn-group" role="group" v-else>
              <button id="btnGroupDrop1" type="button" class="btn btn-secondary btn-small dropdown-toggle" data-bs-toggle="dropdown" data-bs-popper-config='{"strategy":"fixed"}' aria-expanded="false">
                Document Actions
              </button>
              <ul class="dropdown-menu" aria-labelledby="btnGroupDrop1">
                <li><a class="dropdown-item text-decoration-none" href="javascript:void(0)" @click.prevent="viewDocument(item)">View/Download Document</a></li>
                <li><a class="dropdown-item text-decoration-none" href="javascript:void(0)" @click.prevent="uploadDocument(item)">Replace Document</a></li>
                <li><a class="dropdown-item text-decoration-none" href="javascript:void(0)" @click.prevent="deleteDocument(item)">Remove Document</a></li>
              </ul>
            </div>
        </template>
    </b-grid>

    <input
        ref="fileInputRef"
        type="file"
        style="display: none"
        @change="handleFileSelected" />

    <system-alerts></system-alerts>
</template>
<script setup lang="ts">
import {computed, onMounted, ref} from "vue";
import {BTableColumn} from "./BTable/BTable.types";
import {FormModal, ConfirmModal, SystemAlerts} from "./Misc";
import BGrid from "./BGrid/BGrid.vue";
import type {AgencyStaffing, EmployeeData} from "@/types";
import {useFormValidation} from "@/validation";
import {number, object, string} from "yup";
import {BInput, BSelect} from "./Fields";
import {BButton} from "./BButton";
import {BAlert} from "./BAlert";
import {useConfirmDialog} from "@/composable/useConfirmDialog";
import {useEmployeeDataStore} from "@/stores/employeeDataStore";
import {storeToRefs} from "pinia";
import { handleErrorInResponse } from "@/utils";

const props = withDefaults(
    defineProps<{
        title: string;
        stateCode: string;
    }>(),
    {
        title: '',
        stateCode: ''
    }
);

const employeeDataStore = useEmployeeDataStore();
const {loading, getDetailsForCurrentDataset} = storeToRefs(employeeDataStore);
const {confirm} = useConfirmDialog();
const {handleSubmit, errors, resetValidation} = useFormValidation();

const isShowEditEditor = ref(false);
const isEditMode = ref(false);
const selectedOri = ref("");
const oriOptions = ref<{value: string; text: string}[]>([]);
// The "no agencies" error is only valid once the ori request has actually returned.
const oriListLoaded = ref(false);
const record = ref<EmployeeData>({} as EmployeeData);
const currentYear = new Date().getFullYear();
// Year bounds for the Add dialog, taken from the agency's PE start/end dates (never beyond the current year
// or the 25 year look-back the schema allows).
const minYear = ref(currentYear - 25);
const maxYear = ref(currentYear);
const hasYearChoice = computed(() => minYear.value < maxYear.value);

const columns= ref<BTableColumn[]>([
    {
        key: "",
        text: "",
        sortable: false,
        columnClass: ["align-top text-center"],
        class: (item) => ["text-nowrap"],
        children:[
            {
                key: "year",
                text: "Year",
                sortable: false,
                columnClass: ["align-top text-center"],
                class: (item) => ["text-nowrap","text-center"],
                value: (item) => item.year,
            }
        ],
        subRows:(item) => [item],
    },
    {
        key: "fullTimeOfficers",
        text: "Full-time Enforcement Officers",
        sortable: false,
        columnClass: ["align-top text-center"],
        class: (item) => ["text-nowrap","text-center"],
        children:[
            {
                key: "sworn_in_male",
                text: "Male",
                sortable: false,
                columnClass: ["align-top text-center"],
                class: (item) => ["text-nowrap","text-center"],
                value: (item) => item.sworn_in_male,
            },
            {
                key: "sworn_in_female",
                text: "Female",
                sortable: false,
                columnClass: ["align-top text-center"],
                class: (item) => ["text-nowrap","text-center"],
                value: (item) => item.sworn_in_female,
            }
        ]
    },
    {
        key: "fullTimeCivilians",
        text: "Full-time Civilians",
        sortable: false,
        columnClass: ["align-top text-center"],
        class: (item) => ["text-nowrap","text-center"],
        children:[
            {
                key: "civilian_male",
                text: "Civilian - Male",
                sortable: false,
                columnClass: ["align-top text-center"],
                class: (item) => ["text-nowrap","text-center"],
                value: (item) => item.civilian_male,
            },
            {
                key: "civilian_female",
                text: "Civilian - Female",
                sortable: false,
                columnClass: ["align-top text-center"],
                class: (item) => ["text-nowrap","text-center"],
                value: (item) => item.civilian_female,
                
            }
        ]
    }
]);

const agencyLabel = computed(() => {
    return oriOptions.value.find((o) => o.value == selectedOri.value)?.text ?? selectedOri.value;
});

const editDialogTitle = computed(() => {
    return isEditMode.value ? `Edit Police Employee Data for ${record.value.year}` : "Add New Police Employee Data";
});

const schema = object<EmployeeData>({
    year: number()
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .integer()
        .test("year-min", () => `Year must be ${minYear.value} or later`, (value) => value == null || value >= minYear.value)
        .test("year-max", () => `Year must be ${maxYear.value} or earlier`, (value) => value == null || value <= maxYear.value)
        .required()
        .label("Year"),
    sworn_in_male: number()
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .integer()
        .min(0)
        .max(99999)
        .required()
        .label("Full-Time Sworn - Male"),
    sworn_in_female: number()
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .integer()
        .min(0)
        .max(99999)
        .required()
        .label("Full-Time Sworn - Female"),
    civilian_male: number()
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .integer()
        .min(0)
        .max(99999)
        .required()
        .label("Full-Time Civilian - Male"),
    civilian_female: number()
        .transform((value) => (Number.isNaN(value) ? undefined : value))
        .integer()
        .min(0)
        .max(99999)
        .required()
        .label("Full-Time Civilian - Female"),
}).test("total-under-max", function (value) {
    const total =
        (value.sworn_in_male ?? 0) + (value.sworn_in_female ?? 0) + (value.civilian_male ?? 0) + (value.civilian_female ?? 0);

    if (total < 100000) {
        return true;
    }

    return this.createError({
        path: "total",
        message: "The total of all full-time officers and employees must be less than 100,000.",
    });
});

if(props.stateCode == "CO"){
     columns.value.push({
        key: "",
        text: "Budgeted for next year",
        sortable: false,
        columnClass: ["align-top text-center"],
        class: (item) => ["text-nowrap"],
        children: [
            {
                key: "certified_officers_count",
                text: "Certified Officers",
                sortable: false,
                columnClass: ["align-top text-center"],
                class: (item) => ["text-nowrap","text-center"],
                value: (item) => item.certified_officers ?? "",
            },
            {
                key: "non_certified_officers_count",
                text: "Non-Certified Officers",
                sortable: false,
                columnClass: ["align-top text-center"],
                class: (item) => ["text-nowrap","text-center"],
                value: (item) => item.non_certified_officers ?? "",
            },
            {
                key: "lea_budget",
                text: "LEA Budget",
                sortable: false,
                columnClass: ["align-top text-center"],
                class: (item) => ["text-nowrap","text-center"],
                value: (item) => formatBudget(item.lea_budget),
            }
        ]});

        schema.shape.certified_officers = string()
        .trim()
        .matches(/^\d{0,5}$/, "Certified Officers must be a whole number using digits only (maximum 5 digits), or left blank.")
        .label("Certified Officers");

        schema.shape.non_certified_officers = string()
        .trim()
        .matches(/^\d{0,5}$/, "Non-Certified Officers must be a whole number using digits only (maximum 5 digits), or left blank.")
        .label("Non-Certified Officers");

        schema.shape.lea_budget = string()
        .trim()
        .matches(/^\d{0,9}$/, "LEA Budget must be a whole dollar amount of no more than $999,999,999, or left blank.")
        .label("LEA Budget");

}

columns.value.push({
        key: "actionGroup",
        text: "",
        sortable: false,
        columnClass: ["align-top"],
        class: (item) => ["text-nowrap"],
        children:[
            {
                key: "edit",
                text: "",
                sortable: false,
                columnClass: ["align-top"],
                class: (item) => ["text-nowrap"],
            },
            {
                key: "delete",
                text: "",
                sortable: false,
                columnClass: ["align-top"],
                class: (item) => ["text-nowrap"],
            },
            {
                key: "document",
                text: "",
                sortable: false,
                columnClass: ["align-top"],
                class: (item) => ["text-nowrap"],
            }
        ]
    });

const loadOris = async () => {
    const data = await employeeDataStore.getOriListForDataset();
    oriOptions.value = (data ?? []).map((o) => ({value: o.ori, text: `${o.ori} - ${o.label}`}));
    oriListLoaded.value = true;

    if (!selectedOri.value && oriOptions.value.length > 0) {
        selectedOri.value = oriOptions.value[0].value;
    }
};

// Add/edit/delete update the grid optimistically (see employeeDataStore) instead of reloading from the
// server, since the actual write goes through an async processing queue - reloading right after would
// just show stale data. loadGridData is only needed for the initial load and when switching agencies.
const loadGridData = async () => {
    try{
        if (selectedOri.value) {
            await employeeDataStore.list(selectedOri.value);
        }
    }
    catch(error) {
        handleErrorInResponse(error);
    }
};

// Dates arrive as ISO strings; read the year straight from the text so time zones can't shift it.
const yearOf = (value?: string | Date | null): number | null => {
    const year = value ? parseInt(value.toString().substring(0, 4), 10) : NaN;
    return Number.isNaN(year) ? null : year;
};

const getAgencyDatasetDetails = async () => {
    minYear.value = currentYear - 25;
    maxYear.value = currentYear;

    if (!selectedOri.value) {
        return;
    }

    try {
        await employeeDataStore.fetchDatasetDetailsForOri(selectedOri.value);
        const details = getDetailsForCurrentDataset.value;
        const startYear = yearOf(details.agency_start_date) ?? yearOf(details.start_date);
        const endYear = yearOf(details.agency_end_date) ?? yearOf(details.end_date);

        if (startYear) {
            minYear.value = Math.max(minYear.value, startYear);
        }
        if (endYear) {
            maxYear.value = Math.min(maxYear.value, endYear);
        }
    }
    catch(error) {
        handleErrorInResponse(error);
    }
};

const handleOriChange = async () => {
    await loadGridData();
    await getAgencyDatasetDetails();
};

const showAddEditor = () => {
    isEditMode.value = false;
    resetValidation();
    record.value = {
        ori: selectedOri.value,
        year: maxYear.value,
        sworn_in_male: 0,
        sworn_in_female: 0,
        civilian_male: 0,
        civilian_female: 0,
        certified_officers: "",
        non_certified_officers: "",
        lea_budget: "",
    };
    isShowEditEditor.value = true;
};

const showEditEditor = (item: AgencyStaffing) => {
    isEditMode.value = true;
    resetValidation();
    record.value = {
        ori: selectedOri.value,
        year: item.year,
        sworn_in_male: item.sworn_in_male,
        sworn_in_female: item.sworn_in_female,
        civilian_male: item.civilian_male,
        civilian_female: item.civilian_female,
        certified_officers: item.certified_officers?.toString() ?? "",
        non_certified_officers: item.non_certified_officers?.toString() ?? "",
        lea_budget: item.lea_budget?.toString() ?? "",
    };
    isShowEditEditor.value = true;
};

// record.lea_budget always holds bare digits (what the validator and the back-end see); the "$###,###,###"
// formatting only exists on the way in and out of the input and in the grid.
const cleanBudget = (value: string | number | null | undefined) => (value ?? "").toString().replace(/\D/g, "");

const formatBudget = (value: string | number | null | undefined) => {
    const digits = cleanBudget(value);
    return digits === "" ? "" : `$${Number(digits).toLocaleString("en-US")}`;
};

const leaBudgetDisplay = computed({
    get: () => formatBudget(record.value.lea_budget),
    set: (value: string) => {
        record.value.lea_budget = cleanBudget(value).slice(0, 9);
    },
});

const saveRecord = async () => {
    record.value.lea_budget = cleanBudget(record.value.lea_budget);
    if (await handleSubmit(schema, record.value)) {
        if (isEditMode.value) {
            await employeeDataStore.update(props.stateCode, record.value);
        } else {
            await employeeDataStore.create(props.stateCode, record.value);
        }

        isShowEditEditor.value = false;
    }
};

const deleteRecord = async (item: AgencyStaffing) => {
    const shouldProceed = await confirm("Delete", "Are You Sure?");
    if (shouldProceed) {
        await employeeDataStore.deleteItem(props.stateCode, selectedOri.value, item.year);
    }
};

const fileInputRef = ref<HTMLInputElement | null>(null);
const pendingUploadItem = ref<AgencyStaffing | null>(null);

const uploadDocument = (item: AgencyStaffing) => {
    pendingUploadItem.value = item;
    fileInputRef.value?.click();
};

const handleFileSelected = async (event: Event) => {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const item = pendingUploadItem.value;
    input.value = "";
    pendingUploadItem.value = null;

    if (!file || !item) {
        return;
    }

    item.file_id = await employeeDataStore.uploadDocument(selectedOri.value, item.year, file);
};

const viewDocument = (item: AgencyStaffing) => {
    if (!item.file_id) {
        return;
    }
    window.open(`api/v1/police-employee/documents/${item.file_id}`, "_blank");
};

const deleteDocument = async (item: AgencyStaffing) => {
    const shouldProceed = await confirm("Delete", "Are You Sure?");
    if (shouldProceed) {
        await employeeDataStore.deleteDocument(selectedOri.value, item.year);
        item.file_id = null;
    }
};

onMounted(async () => {
    await loadOris();
    await Promise.all([loadGridData(), getAgencyDatasetDetails()]);
});
</script>

<style scoped>
/* Labels sit in a content-sized first column so every input starts right after the longest label in its
   section and the inputs stay aligned with one another. */
.employee-form {
    display: grid;
    grid-template-columns: max-content 8rem;
    column-gap: 1rem;
    row-gap: 0.75rem;
    align-items: start;
}
</style>
